import { supabase } from '../supabase'
import type { CreatePlayerInput, Player } from '@/lib/types/player'
import type { DuplicateCheckResult, RegistrationFormData } from '@/lib/types/registration'
import { calculateAge } from '@/lib/utils/formatters'

export const playerRepository = {
  async checkDuplicatePlayer(params: {
    fullName: string
    mobileNumber: string
  }): Promise<DuplicateCheckResult | null> {
    const { data: player, error } = await supabase
      .from('players')
      .select('id, full_name, mobile_number')
      .eq('full_name', params.fullName)
      .eq('mobile_number', params.mobileNumber)
      .maybeSingle()

    if (error) throw new Error(`Failed to check duplicate: ${error.message}`)
    if (!player) return null

    // Fetch player sports
    const { data: playerSports, error: sportsError } = await supabase
      .from('player_sports')
      .select('sport_id, sports(name)')
      .eq('player_id', player.id)

    if (sportsError) throw new Error(`Failed to fetch sports: ${sportsError.message}`)

    return {
      player_id: player.id,
      sport_names: playerSports?.map((ps: any) => ps.sports.name) || [],
      sport_ids: playerSports?.map((ps: any) => ps.sport_id) || [],
    }
  },

  async createPlayer(input: CreatePlayerInput): Promise<number> {
    const { formData, photoUrl, paymentUrl, expectedAmount } = input

    const buildingValue =
      formData.building === 'Other'
        ? formData.otherBuilding || formData.building
        : formData.building

    const { data, error } = await supabase
      .from('players')
      .insert({
        full_name: formData.fullName,
        building: buildingValue,
        wing_flat: formData.wingFlat,
        mobile_number: formData.mobileNumber,
        dob: formData.dob.toISOString().split('T')[0],
        age: calculateAge(formData.dob),
        gender: formData.gender,
        tshirt_size: formData.tshirtSize,
        tshirt_number: formData.tshirtNumber,
        tshirt_name: formData.tshirtName,
        photo_url: photoUrl,
        sports_expertise: formData.sportsExpertise || null,
        expected_amount: expectedAmount,
        paid_amount: 0,
        payment_status: 'pending',
        payment_url: paymentUrl,
        is_flagged: false,
      })
      .select('id')
      .single()

    if (error) throw new Error(`Failed to create player: ${error.message}`)
    return data.id
  },

  async createPlayerSports(playerId: number, sportIds: number[]): Promise<void> {
    const records = sportIds.map((sportId) => ({
      player_id: playerId,
      sport_id: sportId,
    }))

    const { error } = await supabase.from('player_sports').insert(records)

    if (error) throw new Error(`Failed to create player sports: ${error.message}`)
  },
}
